export function badgeUid(value) {
  if (typeof value !== 'string' || value.length > 96 || !/^[a-f\d:\-\s]*$/i.test(value)) return ''
  const hex = value.replace(/[:\-\s]/g, '').toUpperCase()
  return hex.length >= 2 && hex.length <= 64 && hex.length % 2 === 0 ? hex : ''
}

export function createBadgeScanner(env = window) {
  let active = null
  function finish(session, result) {
    if (active !== session) return
    active = null
    env.clearTimeout(session.timer)
    if (session.controller) session.controller.abort()
    if (session.reader) { session.reader.onreading = null; session.reader.onreadingerror = null }
    if (session.native) {
      try { Promise.resolve(env.flutter_inappwebview.callHandler('posBadgeCancel', { bridgeToken: session.token })).catch(() => {}) } catch (_) { /* The local scan still ends if the bridge has gone away. */ }
    }
    session.resolve(result)
  }
  function cancel() { if (active) finish(active, { status: 'cancelled' }) }
  function start(options = {}) {
    if (active) return Promise.resolve({ status: 'busy' })
    return new Promise(resolve => {
      const session = { resolve, token: env.__wellcomePosBadgeToken, native: false }
      active = session
      session.timer = env.setTimeout(() => finish(session, { status: 'timeout' }), 65000)
      if (env.document && env.document.hidden) { finish(session, { status: 'cancelled' }); return }
      if (session.token && env.flutter_inappwebview) {
        session.native = true
        ;(async () => {
          try {
            const options = { bridgeToken: session.token }
            const info = await env.flutter_inappwebview.callHandler('posBadgeCapabilities', options)
            if (active !== session) return
            if (!info || !info.supported) { finish(session, { status: 'unsupported' }); return }
            if (!info.enabled) { finish(session, { status: 'disabled' }); return }
            const result = await env.flutter_inappwebview.callHandler('posBadgeScan', options)
            if (active !== session) return
            finish(session, result && result.status === 'read' ? { status: 'read', transport: 'android_native', uidHex: badgeUid(result.uidHex),
              technologies: Array.isArray(result.technologies) ? result.technologies.filter(row => typeof row === 'string').slice(0, 10).map(row => row.slice(0, 40)) : [] } : { status: result && ['timeout', 'cancelled', 'disabled', 'unsupported'].includes(result.status) ? result.status : 'error' })
          } catch (_) { finish(session, { status: 'error' }) }
        })()
        return
      }
      if (!env.isSecureContext) { finish(session, { status: 'insecure' }); return }
      if (!env.NDEFReader || env !== env.top) { finish(session, { status: env.flutter_inappwebview ? 'app_update' : 'unsupported' }); return }
      function scanWeb() {
        if (active !== session) return
        if (env.document && env.document.hidden) { finish(session, { status: 'cancelled' }); return }
        try {
          session.controller = new env.AbortController()
          session.reader = new env.NDEFReader()
          session.reader.onreading = event => finish(session, { status: 'read', transport: 'web_nfc', uidHex: badgeUid(event.serialNumber), technologies: ['NDEF'] })
          session.reader.onreadingerror = () => finish(session, { status: 'unreadable', transport: 'web_nfc' })
          session.reader.scan({ signal: session.controller.signal }).then(() => {
            if (active === session && options.continuous === true) { env.clearTimeout(session.timer); session.timer = null }
          }).catch(error => {
            finish(session, { status: error.name === 'NotAllowedError' ? 'denied' : error.name === 'NotSupportedError' ? 'unsupported' : 'error' })
          })
        } catch (_) { finish(session, { status: 'error' }) }
      }
      // Keep the first permission request inside the real click handler. Automatic scans never prompt.
      if (options.automatic !== true) { scanWeb(); return }
      ;(async () => {
        try {
          const permissions = env.navigator && env.navigator.permissions
          if (!permissions) { finish(session, { status: 'permission_required' }); return }
          const permission = await permissions.query({ name: 'nfc' })
          if (active !== session) return
          if (!permission || permission.state !== 'granted') { finish(session, { status: permission && permission.state === 'denied' ? 'denied' : 'permission_required' }); return }
          scanWeb()
        } catch (_) { finish(session, { status: 'permission_required' }) }
      })()
    })
  }
  return { start, cancel }
}
